import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Search, Check, ArrowRight, Wrench, SearchX, Plus, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { RequestTool } from "@/components/RequestTool";
import { getIcon } from "@/lib/icons";

const TILE = {
  lime: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
  cyber: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  tangerine: "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
};
const WASH = { lime: "bg-emerald-400", pink: "bg-pink-400", cyber: "bg-indigo-400", tangerine: "bg-orange-400" };
const tile = (c) => TILE[c] || TILE.cyber;
const wash = (c) => WASH[c] || WASH.cyber;

function ToolCard({ tool, selected, onToggle, index }) {
  const Icon = getIcon(tool.icon);
  const CatIcon = getIcon(tool.catIcon);
  return (
    <motion.button
      data-testid={`tool-card-${tool.slug}`}
      onClick={() => onToggle(tool.slug)}
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.025, 0.3) }}
      whileTap={{ scale: 0.98 }}
      className={`group relative overflow-hidden text-left rounded-2xl border p-5 brutal-lift ${
        selected
          ? "beam-top border-primary ring-2 ring-primary/25 bg-primary/[0.05]"
          : "border-border bg-card hover:border-primary/40"
      }`}
    >
      <span className={`orb -top-12 -right-10 h-28 w-28 ${wash(tool.color)} ${selected ? "opacity-60" : "opacity-0 group-hover:opacity-50"} transition-opacity duration-300`} />
      <div className="relative flex items-start justify-between">
        <span className={`grid place-items-center h-12 w-12 rounded-2xl ${tile(tool.color)}`}>
          <Icon className="h-6 w-6" />
        </span>
        <span className={`grid place-items-center h-6 w-6 rounded-full border-2 transition-colors ${
          selected ? "bg-primary border-primary text-white" : "border-border group-hover:border-primary/50"
        }`}>
          {selected
            ? <span data-testid={`tool-selected-${tool.slug}`}><Check className="h-3.5 w-3.5" /></span>
            : <Plus className="h-3.5 w-3.5 text-muted-foreground" />}
        </span>
      </div>
      <h3 className={`relative mt-4 font-heading font-bold text-lg leading-tight transition-colors ${selected ? "text-primary" : "text-foreground group-hover:text-primary"}`}>
        {tool.name}
      </h3>
      <p className="relative mt-1.5 text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{tool.tagline}</p>
      <span className="relative mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <CatIcon className="h-3 w-3" /> {tool.catLabel}
      </span>
    </motion.button>
  );
}

export default function ToolsCatalogue() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/tools").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const toggle = (slug) =>
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));

  const allTools = useMemo(() => {
    if (!data) return [];
    return data.categories.flatMap((c) =>
      c.tools.map((t) => ({ ...t, catKey: c.key, catLabel: c.label, catIcon: c.icon }))
    );
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTools.filter(
      (t) =>
        (activeCat === "all" || t.catKey === activeCat) &&
        (!q || t.name.toLowerCase().includes(q) || (t.tagline || "").toLowerCase().includes(q))
    );
  }, [allTools, query, activeCat]);

  const start = () => {
    if (selected.length === 0) return;
    navigate(`/tools/course?tools=${selected.join(",")}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const filters = [
    { key: "all", label: "All tools", icon: null, count: allTools.length },
    ...data.categories.map((c) => ({ key: c.key, label: c.label, icon: c.icon, count: c.tools.length })),
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="tools-catalogue">
      <Navbar />

      {/* vibrant hero */}
      <div className="relative overflow-hidden mesh-hero border-b border-border/60">
        <span className="orb top-[-6rem] left-[-4rem] h-72 w-72 bg-indigo-400 floaty-slow" />
        <span className="orb top-[-2rem] right-[6rem] h-56 w-56 bg-pink-400 floaty" />
        <PageTransition className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-12">
          <div className="max-w-3xl" data-testid="tools-header">
            <p className="flex items-center gap-2 text-primary font-bold tracking-[0.2em] text-xs uppercase mb-4">
              <Wrench className="h-4 w-4" /> Tool mode
            </p>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[0.98]">
              Master any <span className="gradient-text">AI tool</span>, end to end.
            </h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Pick the tools you care about — we'll build a complete, structured course on each, from the basics to a real project you can ship.
            </p>
          </div>
        </PageTransition>
      </div>

      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* sticky category sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                <input
                  data-testid="tools-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${allTools.length} tools…`}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-border bg-card shadow-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
              <div>
                <p className="hidden lg:block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 px-1">Categories</p>
                <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-1 px-1 pb-1 no-scrollbar">
                  {filters.map((f) => {
                    const FIcon = f.icon ? getIcon(f.icon) : Sparkles;
                    const on = activeCat === f.key;
                    return (
                      <button
                        key={f.key}
                        data-testid={`tools-filter-${f.key}`}
                        onClick={() => setActiveCat(f.key)}
                        className={`shrink-0 lg:w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-all brutal-press ${
                          on ? "bg-primary text-primary-foreground shadow-brutal-cyber" : "bg-card border border-border text-foreground hover:border-primary/40 hover:text-primary"
                        }`}
                      >
                        <FIcon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 text-left whitespace-nowrap lg:whitespace-normal">{f.label}</span>
                        <span className={`text-xs font-bold tabular-nums ${on ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{f.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <RequestTool className="hidden lg:flex w-full" />
            </div>
          </aside>

          {/* bento tool grid */}
          <main className="lg:col-span-9">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center border-2 border-dashed border-border rounded-3xl" data-testid="tools-no-results">
                <SearchX className="h-14 w-14 text-muted-foreground/50 mb-4" />
                <h3 className="font-heading font-bold text-xl text-foreground">No tools found</h3>
                <p className="mt-1.5 text-muted-foreground">Try another name or pick a different category.</p>
                <RequestTool defaultName={query} className="mt-5" />
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Showing <b className="text-foreground">{filtered.length}</b> tool{filtered.length > 1 ? "s" : ""}
                  {selected.length > 0 && <> · <span className="text-primary font-semibold">{selected.length} selected</span></>}
                </p>
                <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filtered.map((t, i) => (
                    <ToolCard key={t.slug} tool={t} selected={selected.includes(t.slug)} onToggle={toggle} index={i} />
                  ))}
                </motion.div>
              </>
            )}
          </main>
        </div>
      </PageTransition>

      {/* floating selection bar */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0, x: "-50%" }}
            animate={{ y: 0, opacity: 1, x: "-50%" }}
            exit={{ y: 100, opacity: 0, x: "-50%" }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 md:bottom-6 left-1/2 z-40 flex items-center gap-4 sm:gap-5 px-5 py-3 rounded-full bg-foreground text-background shadow-2xl w-[92%] sm:w-auto max-w-xl"
            data-testid="tools-start-bar"
          >
            <button data-testid="tools-clear" onClick={() => setSelected([])}
              className="text-sm font-medium text-background/60 hover:text-background transition-colors shrink-0">
              Clear
            </button>
            <p className="font-medium text-sm sm:text-base flex-1 sm:flex-none truncate">
              {selected.length} tool{selected.length > 1 ? "s" : ""} selected
            </p>
            <button data-testid="tools-start-btn" onClick={start}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 hover:scale-105 transition-all glow-cyber whitespace-nowrap shrink-0">
              Build my course <ArrowRight className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
